import * as Yup from 'yup';
import dayjs from 'dayjs';

import { FieldProps } from '~components/form-fields';

import { getAll as getUsers } from '~care-workers/actions';

import { selectLookupUsers } from '~care-workers/selectors/users';

export interface ReviewFormValues {
  review_type_id: string;
  review_date: string;
  reviewer_id: number | null;
  trigger_type_id: string;
  attendees_description: string;
  summary: string;
  outcome: string;
  review_due_date: string;
}

interface ReviewFormOptions {
  reviewTypeOptions: { id: string; name: string }[];
  triggerOptions: { id: string; name: string }[];
  /** Id of the "Triggered" review type — the trigger-reason field shows only for it. */
  triggeredTypeId?: string;
}

// Record-review form for the Care Plans Reviews tab. Review type + trigger reason come
// from seeded lookups; the trigger field is shown only when the "Triggered" type is
// selected. "Next review scheduled" defaults to 12 months after the review date at save
// (handled in the component). Reviewed-by lists staff via the shared users lookup.
// Fields render full-width, one per row (no colSize), matching the need/goal modals.
export const reviewFormFields = ({ reviewTypeOptions, triggerOptions, triggeredTypeId }: ReviewFormOptions): FieldProps => ({
  review_date: {
    type: 'date',
    caption: 'Review date',
    required: true,
    dataCy: 'care-plan-review-date',
  },
  reviewer_id: {
    type: 'select',
    caption: 'Reviewed by',
    required: true,
    selectorOptions: selectLookupUsers,
    selectorActions: getUsers,
    placeholder: '-- Select a reviewer --',
    dataCy: 'care-plan-review-reviewer',
  },
  review_type_id: {
    type: 'select',
    caption: 'Review type',
    required: true,
    options: reviewTypeOptions,
    placeholder: '-- Select a type --',
    dataCy: 'care-plan-review-type',
  },
  trigger_type_id: {
    type: 'select',
    caption: 'Trigger reason',
    // Only rendered for triggered reviews (see show), and required when it is.
    required: true,
    options: triggerOptions,
    placeholder: '-- Select a reason --',
    show: (values) => !!triggeredTypeId && values.review_type_id === triggeredTypeId,
    dataCy: 'care-plan-review-trigger',
  },
  attendees_description: {
    type: 'text',
    caption: 'Attendees',
    dataCy: 'care-plan-review-attendees',
  },
  summary: {
    type: 'textarea',
    caption: 'Summary',
    dataCy: 'care-plan-review-summary',
  },
  outcome: {
    type: 'text',
    caption: 'Outcome',
    dataCy: 'care-plan-review-outcome',
  },
  review_due_date: {
    type: 'date',
    caption: 'Next review date',
    description: 'Defaults to 12 months after the review date if left blank.',
    dataCy: 'care-plan-review-next-due',
  },
});

// Factory: the trigger reason is required only for the triggered review type, so the
// schema needs that id. Review date, reviewer and review type are always required.
export const reviewFormSchema = (triggeredTypeId?: string) =>
  Yup.object().shape({
    review_date: Yup.string().required('Review date is required'),
    reviewer_id: Yup.number().nullable().required('Reviewer is required'),
    review_type_id: Yup.string().required('Review type is required'),
    trigger_type_id: Yup.string().test(
      'trigger-required-when-triggered',
      'Trigger reason is required',
      (value, ctx) => {
        const isTriggered = !!triggeredTypeId && ctx.parent.review_type_id === triggeredTypeId;
        return !isTriggered || !!value;
      },
    ),
  });

// Normalise form values into the create-review body. "Next review scheduled" defaults
// to 12 months after the review date when blank; the trigger reason is dropped unless
// the review is triggered; empty optional text becomes null.
export const buildReviewCreateBody = (values: ReviewFormValues, triggeredTypeId?: string) => {
  const isTriggered = !!triggeredTypeId && values.review_type_id === triggeredTypeId;
  return {
    review_type_id: values.review_type_id,
    review_date: values.review_date,
    reviewer_id: values.reviewer_id ?? null,
    trigger_type_id: isTriggered ? values.trigger_type_id || null : null,
    attendees_description: values.attendees_description || null,
    summary: values.summary || null,
    outcome: values.outcome || null,
    review_due_date: values.review_due_date || dayjs(values.review_date).add(1, 'year').format('YYYY-MM-DD'),
  };
};
